import {parseWithZod} from '@conform-to/zod'
import {type ActionFunctionArgs} from '@remix-run/node'
import {json} from '@remix-run/react'
import {HTTPError} from 'got'
import {z} from 'zod'

import {
  createMemory,
  deleteMemory,
  updateMemory,
} from '~/models/memory/memory.server'
import {getUserToken} from '~/utils/auth'
import {
  getTranslateDefaults,
  setTranslateDefaults,
} from '~/utils/defaults.server'

export const addMemorySchema = z.object({
  name: z.string().min(1),
})

const addMemoryAction = async ({request}: ActionFunctionArgs) => {
  const token = await getUserToken(request)
  if (!token) return

  const formData = await request.formData()

  const submission = await parseWithZod(formData, {
    async: true,
    schema: addMemorySchema.transform(async (value, ctx) => {
      try {
        return await createMemory({name: value.name, token})
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            error instanceof HTTPError && error.response.statusCode == 400
              ? 'Error creating memory'
              : 'Unexpected error',
        })
        return z.NEVER
      }
    }),
  })
  const memoryId = submission.status == 'success' ? submission.value.id : null
  if (!memoryId) return json(submission.reply())

  const {activeMemories, ...defaults} = await getTranslateDefaults({request})
  activeMemories.push(memoryId)

  return json(submission.reply(), {
    headers: {
      'Set-Cookie': await setTranslateDefaults({
        activeMemories,
        ...defaults,
      }),
    },
  })
}

export type AddMemoryAction = typeof addMemoryAction

export const schema = z.object({
  isActive: z
    .string()
    .nullable()
    .transform((val) => val == 'on'),
  memoryId: z.string(),
})

const toggleMemoryAction = async ({request}: ActionFunctionArgs) => {
  const {activeMemories: memories, ...defaults} = await getTranslateDefaults({
    request,
  })

  const formData = await request.formData()
  const isActive = formData.get('isActive')
  const memoryId = formData.get('memoryId')
  const parsedData = schema.parse({isActive, memoryId})

  const memoryIndex = memories.findIndex(
    (m) => m.toString() == parsedData.memoryId,
  )
  if (memoryIndex == -1 && isActive) {
    memories.push(parseInt(parsedData.memoryId, 10))
  }

  if (memoryIndex != -1 && !isActive) {
    memories.splice(memoryIndex, 1)
  }

  const serializedCookies = await setTranslateDefaults({
    activeMemories: memories,
    ...defaults,
  })

  return json(
    {
      data: {memories},
    },
    {
      headers: {
        'Set-cookie': serializedCookies,
      },
    },
  )
}

export type ToggleMemoryAction = typeof toggleMemoryAction

export const deleteMemorySchema = z.object({
  id: z.string().min(1),
})

const deleteMemoryAction = async ({request}: ActionFunctionArgs) => {
  const token = await getUserToken(request)
  if (!token) return

  const formData = await request.formData()

  const submission = await parseWithZod(formData, {
    async: true,
    schema: deleteMemorySchema.transform(async (value, ctx) => {
      try {
        return await deleteMemory({id: parseInt(value.id, 10), token})
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            error instanceof HTTPError && error.response.statusCode == 400
              ? 'Error deleting memory'
              : 'Unexpected error',
        })
        return z.NEVER
      }
    }),
  })

  const memoryId = submission.status == 'success' ? submission.value.id : null
  if (!memoryId) return json(submission.reply())

  const {activeMemories, ...defaults} = await getTranslateDefaults({request})
  const memoryIndex = activeMemories.findIndex((m) => m == memoryId)
  if (memoryIndex != -1) {
    activeMemories.splice(memoryIndex, 1)
  }

  return json(submission.reply(), {
    headers: {
      'Set-Cookie': await setTranslateDefaults({activeMemories, ...defaults}),
    },
  })
}
export type DeleteMemoryAction = typeof deleteMemoryAction

export const updateMemorySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
})

const updateMemoryAction = async ({request}: ActionFunctionArgs) => {
  const token = await getUserToken(request)
  if (!token) return

  const formData = await request.formData()

  const submission = await parseWithZod(formData, {
    async: true,
    schema: updateMemorySchema.transform(async (value, ctx) => {
      try {
        return await updateMemory({
          payload: {name: value.name, id: parseInt(value.id, 10)},
          token,
        })
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            error instanceof HTTPError && error.response.statusCode == 400
              ? 'Error updating memory'
              : 'Unexpected error',
        })
        return z.NEVER
      }
    }),
  })
  return json(submission.reply())
}

export type UpdateMemoryAction = typeof updateMemoryAction

export const action = async (args: ActionFunctionArgs) => {
  const formData = await args.request.clone().formData()

  const intent = formData.get('_action')

  if (intent == 'addMemory') {
    return addMemoryAction(args)
  }

  if (intent == 'deleteMemory') {
    return deleteMemoryAction(args)
  }

  if (intent == 'updateMemory') {
    return updateMemoryAction(args)
  }

  if (intent == 'toggleMemory') {
    return toggleMemoryAction(args)
  }

  throw new Error('Invalid intent')
}
